import React, { useEffect } from 'react';
import { useQuery } from 'react-apollo';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Label } from '_Cards/FilterCards/DataByFiltersCard/_Containers/Dropdowns/utils/DropdownStyles';
import queryGetEmotionalTriggersTable from '_Resources/Triggers/Services/graphql/getEmotionalTriggersTable';
import { setActiveTriggersTable } from '_Resources/Triggers/Actions/setActiveTriggersTable';
import { setActiveTriggersOnlySelectedThematics } from '_Resources/Triggers/Actions/setActiveTriggersOnlySelectedThematics';
import QSelect from '_Components/Fields/QSelect/QSelect';
import { get } from 'lodash';
import { selectThematicSelection } from '_Resources/Cards/Actions/selectThematicSelection';
import { setThematicSelectionList } from '_Resources/Cards/Actions/setThematicSelectionList';
import { grayShades } from '../../../styles/abstracts/colors';

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  width: 15rem;
`;

const LabelSpan = styled(Label)`
  font-size: 0.9rem;
  color: ${grayShades.g800};
`;

const SettingDiv = styled.span`
  display: flex;
  align-items: center;
  justify-content: space-between;
  // margin: 2em;
  font-weight: bold;
  width: 26rem;
  margin-right: 2.5rem;
`;

const ThematicSelectionSelect = styled(QSelect)``;

type ThematicType = {
  thematic: string;
  subThematic: string;
  subSubThematic: string;
};

type ThematicSelectionType = {
  id: string | null;
  label: string;
  name: string;
  projectId: number;
  thematics: Array<ThematicType>;
  createdBy: {
    firstName: string;
    lastName: string;
    email: string;
  };
};

type TriggersThematicsSelectionDropdownType = {
  t: Function;
  id: number;
  dispatchSetActiveTriggersTable: Function;
  dispatchSetActiveTriggersOnlySelectedThematics: Function;
  activeTriggersOnlySelectedThematics: boolean;
  activeTriggersTable: {
    id: number;
    title: string;
    description: string;
    isDefault: boolean;
    createdBy: {
      email: string;
      firstName: string;
      lastName: string;
    };
    settings: {
      analysisAxis: string;
      calculationCriteria: string;
      itemsToDisplay: {
        name: string;
        key: string;
      };
      significanceThreshold: number;
      range: {
        startDate: string;
        endDate: string;
      };
      thematics: {
        thematic: string;
        subThematic: string;
        subSubThematic: string;
      }[];
      criteriaKeys: {
        name: string;
        values: string[];
        id: string;
        label: string;
      }[];
    };
    data: {
      color: string;
      originalColor: string;
      name: string;
      originalName: string;
      items: {
        name: string;
        originalName: string;
        percentage: number;
        value: number;
      }[];
      value: number;
      deleted: boolean;
      masked: boolean;
    }[];
    customizations: {
      color: string;
      originalColor: string;
      name: string;
      originalName: string;
      items: {
        name: string;
        originalName: string;
      }[];
      deleted: boolean;
      masked: boolean;
    }[];
  };
  selectedThematicSelectionOption: ThematicSelectionType;
  dispatchSelectThematicSelection: Function;
  dispatchSetThematicSelectionList: Function;
  projectId: number;
  settings: any;
  setSettings: Function;
  onThematicSelectionChange: Function;
  currentItem: any;
  setCurrentItem: Function;
  thematicSelectionsOptions: Array<ThematicSelectionType>;
};

const TriggersThematicsSelectionDropdown = (props: TriggersThematicsSelectionDropdownType) => {
  const {
    t,
    id,
    dispatchSetActiveTriggersTable,
    activeTriggersOnlySelectedThematics,
    selectedThematicSelectionOption,
    onThematicSelectionChange,
    currentItem,
    thematicSelectionsOptions,
  } = props;
  const { data: emotionalTriggersTable, loading, error, refetch } = useQuery(queryGetEmotionalTriggersTable, {
    variables: {
      id,
      thematicSelectionId:
        selectedThematicSelectionOption?.id !== null ? selectedThematicSelectionOption?.id : undefined,
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!loading && !error) {
      const tableToDispatch = emotionalTriggersTable?.getEmotionalTriggersTable;
      dispatchSetActiveTriggersTable(tableToDispatch);
    }
  }, [activeTriggersOnlySelectedThematics, emotionalTriggersTable?.getEmotionalTriggersTable, loading]);

  return (
    <SettingDiv>
      <LabelContainer>
        <LabelSpan>{t('Selected categories')}</LabelSpan>
      </LabelContainer>
      <ThematicSelectionSelect
        name="thematicSelectionSelect"
        value={currentItem}
        searchable={false}
        clearable={false}
        placeholder={t('selections:Current selection')}
        onChange={onThematicSelectionChange}
        options={thematicSelectionsOptions}
        width={244}
      />
    </SettingDiv>
  );
};

const mapStateToProps = (state: any) => ({
  id: get(state, ['triggers', 'activeTriggersTable', 'id']),
  activeTriggersTable: get(state, ['triggers', 'activeTriggersTable']),
  activeTriggersOnlySelectedThematics: get(state, ['triggers', 'activeTriggersOnlySelectedThematics']),
  projectId: get(state, ['projects', 'currentProject', 'id']),
});

const mapDispatchToProps = (
  dispatch: (arg0: { type: string; activeTriggersTable?: any; onlySelectedThematicsSwitch?: boolean }) => void,
) => ({
  dispatchSetActiveTriggersTable: (triggersTable: any) => {
    dispatch(setActiveTriggersTable(triggersTable));
  },
  dispatchSetActiveTriggersOnlySelectedThematics: (onlySelectedThematicsSwitch: boolean) => {
    dispatch(setActiveTriggersOnlySelectedThematics(onlySelectedThematicsSwitch));
  },
  dispatchSelectThematicSelection: thematicSelection => {
    dispatch(selectThematicSelection(thematicSelection));
  },
  dispatchSetThematicSelectionList: thematicSelectionList => {
    dispatch(setThematicSelectionList(thematicSelectionList));
  },
});

export default withTranslation(['button', 'triggers'])(
  connect(mapStateToProps, mapDispatchToProps)(TriggersThematicsSelectionDropdown),
);
